import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface Assistant {
  id: string;
  name: string;
  template_id: string | null;
  instructions: string;
  status: 'creating' | 'ready' | 'error';
  created_at: string;
  updated_at: string;
  knowledgeBases?: { id: string; name: string }[];
}

interface AssistantContextType {
  assistants: Assistant[];
  activeAssistantId: string | null;
  activeAssistant: Assistant | null;
  loading: boolean;
  error: string | null;
  setActiveAssistantId: (id: string | null) => void;
  fetchAssistants: () => Promise<void>;
  createAssistant: (name: string, instructions: string, templateId?: string, knowledgeBaseIds?: string[]) => Promise<Assistant>;
  deleteAssistant: (id: string) => Promise<void>;
}

const AssistantContext = createContext<AssistantContextType>({
  assistants: [],
  activeAssistantId: null,
  activeAssistant: null,
  loading: false,
  error: null,
  setActiveAssistantId: () => {},
  fetchAssistants: async () => {},
  createAssistant: async () => { throw new Error('Not initialized.'); },
  deleteAssistant: async () => {},
});

export function useAssistants() {
  return useContext(AssistantContext);
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [activeAssistantId, setActiveAssistantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeAssistant = assistants.find(a => a.id === activeAssistantId) || null;

  const fetchAssistants = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/assistants');
      if (res.ok) {
        const data = await res.json();
        setAssistants(data);
      } else {
        setError('Could not load assistants. Please try again.');
      }
    } catch (err) {
      console.error('Failed to fetch assistants:', err);
      setError('Could not load assistants. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const createAssistant = useCallback(async (
    name: string,
    instructions: string,
    templateId?: string,
    knowledgeBaseIds?: string[]
  ) => {
    const res = await fetch('/api/v1/assistants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, instructions, templateId, knowledgeBaseIds }),
    });
    if (!res.ok) throw new Error('Failed to create assistant.');
    const data = await res.json();
    setAssistants(prev => [data, ...prev]);
    return data;
  }, []);

  const deleteAssistant = useCallback(async (id: string) => {
    const res = await fetch(`/api/v1/assistants/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete assistant.');
    setAssistants(prev => prev.filter(a => a.id !== id));
    if (activeAssistantId === id) setActiveAssistantId(null);
  }, [activeAssistantId]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  return (
    <AssistantContext.Provider
      value={{
        assistants,
        activeAssistantId,
        activeAssistant,
        loading,
        error,
        setActiveAssistantId,
        fetchAssistants,
        createAssistant,
        deleteAssistant,
      }}
    >
      {children}
    </AssistantContext.Provider>
  );
}
